import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { InvoiceDetailsService } from './invoice-details.service';
import { CommonService } from '../common/services/common.service';
import { Subscription } from 'rxjs/internal/Subscription';

@Component({
  selector: 'app-invoice-details',
  templateUrl: './invoice-details.component.html',
  styleUrls: ['./invoice-details.component.scss'],
})
export class InvoiceDetailsComponent implements OnInit {
  form: FormGroup;
  clients: any[] = [];
  projects: any[] = [];
  invoiceNumbers: any[] = [];
  reportUrl: SafeResourceUrl;
  isLoading = false;
  invoiceDetails: any;
  isUpdateEnabled = false;
  updateErrorMessage: string = '';
  activeTab: string = 'invoiceDetails';
  bankDetails: any = null;
  selectedTabIndex = 0;
  private valueChangesSub: Subscription | null = null;


  constructor(
    private fb: FormBuilder,
    private invoiceService: InvoiceDetailsService,
    private commonService: CommonService,
    private sanitizer: DomSanitizer
  ) {
    this.form = this.fb.group({
      client: [''],
      project: [''],
      invoice_number: [''],
      invoice_description: [''],
      invoice_date: [''],
      po_number: [''],
      invoice_contact: [''],
      invoice_address: ['']
    });
    // this.form.valueChanges.subscribe(() => {
    //   this.checkUpdateEligibility();
    // });
  }

  async ngOnInit() {
    await this.loadClients();
  }

  getClientCode(client: string) {
    const clientCodeRegExp = new RegExp('([^-s]+)$');
    return clientCodeRegExp.exec(client)[0].trim();
  }
  switchToTab(index: number) {
    this.selectedTabIndex = index;
  }


  async loadClients() {
    try {
      this.isLoading = true;
      this.clients = await this.commonService.getAllClients('reports');
    } catch (error) {
      console.error('Error loading clients:', error);
    } finally {
      this.isLoading = false;
    }
  }
  async onClientChange() {
    const selectedClient = this.form.get('client').value;
    if (selectedClient) {
        try {
            this.isLoading = true;
            // Find the full client name from the clients array
            const fullClientName = this.clients.find(client => client.Client === selectedClient)?.ClientDescription;
            
            const response = await this.commonService.getFilteredProjectsAndContacts(fullClientName);
    
            this.projects = [];

            // Get the first (and likely only) key from the response
            const clientKey = Object.keys(response)[0];
            if (response[clientKey] && response[clientKey].project_details) {
                this.projects = response[clientKey].project_details.map(project => ({
                    project: project.project,
                    projectCode: project.projectCode,
                    projectDescription: project.projectDescription
                }));
            }
            this.form.patchValue({
                project: '',
                invoice_number: ''
            });
            this.resetInvoiceDetails();
            this.invoiceNumbers = [];

            // If no projects found, log a warning
            if (this.projects.length === 0) {
                console.warn('No projects found for the selected client');
            }
        } catch (error) {
            console.error('Error loading projects:', error);
        } finally {
            this.isLoading = false;
        }
    }
}

onProjectChange() {
  const client = this.form.get('client').value;
  const project = this.form.get('project').value;

  if (client && project) {
      this.isLoading = true;
      this.invoiceService.getInvoiceNumbers(client, project).subscribe({
          next: (invoices) => {
              this.invoiceNumbers = invoices;

              // Reset invoice number selection and details
              this.form.patchValue({
                  invoice_number: ''
              });
              this.resetInvoiceDetails();
          },
          error: (error) => {
              console.error('Error loading invoices:', error);
              this.invoiceNumbers = []; // Clear invoices on error
          },
          complete: () => this.isLoading = false
      });
  }
}

onInvoiceNumberChange() {
  const client = this.form.get('client').value;
  const project = this.form.get('project').value;
  const invoiceNumber = this.form.get('invoice_number').value;

  // Unsubscribe from previous form changes
  if (this.valueChangesSub) {
    this.valueChangesSub.unsubscribe();
    this.valueChangesSub = null;
  }

  if (client && project && invoiceNumber) {
    this.isLoading = true;
    this.invoiceService.getInvoiceDetails(invoiceNumber, client, project).subscribe({
      next: (response) => {
        this.invoiceDetails = response.invoice_details;
        this.reportUrl = this.sanitizer.bypassSecurityTrustResourceUrl(response.report_url);

        // Populate form with invoice details
        if (this.invoiceDetails && this.invoiceDetails.length > 0) {
          const details = this.invoiceDetails[0];

          this.form.patchValue({
            invoice_description: details.Invoice_Description || '',
            invoice_date: this.formatDate(response.invoice_date),
            po_number: details.PO_Number || '',
            invoice_contact: details.Invoice_Contact || '',
            invoice_address: details.Invoice_Address || ''
          });

          // Reset form state
          this.form.markAsPristine();
          this.isUpdateEnabled = false;

          // Set up new value changes subscription
          this.setupFormValueChanges();

          // If BankId is available, fetch bank details
          if (details.BankId) {
            this.fetchBankDetails(details.BankId);
          }
        }
      },
      error: (error) => {
        console.error('Error loading invoice details:', error);
      },
      complete: () => {
        this.isLoading = false;
      }
    });
  }
}

private fetchBankDetails(bankId: number) {
  this.invoiceService.getBankDetails(bankId).subscribe({
    next: (bankDetails) => {
      
      this.bankDetails = bankDetails;
    },
    error: (error) => {
      console.error('Error loading bank details:', error);
    }
  });
}
private setupFormValueChanges() {
  this.valueChangesSub = this.form.valueChanges.subscribe(() => {
    if (this.form.get('invoice_number').value) {
      this.checkUpdateEligibility();
    }
  });
}
ngOnDestroy() {
  if (this.valueChangesSub) {
    this.valueChangesSub.unsubscribe();
  }
}
checkUpdateEligibility() {
  if (!this.form.dirty) {
    this.isUpdateEnabled = false;
    return;
  }
  // Enable update only if invoice details form fields have changed
  const originalDetails = this.invoiceDetails && this.invoiceDetails.length > 0 ? this.invoiceDetails[0] : {};

  this.isUpdateEnabled =
    this.form.get('invoice_description').value !== (originalDetails.Invoice_Description || '') ||
    this.form.get('invoice_date').value !== (originalDetails.Invoice_Date ? this.formatDate(originalDetails.Invoice_Date) : '') ||
    this.form.get('po_number').value !== (originalDetails.PO_Number || '') ||
    this.form.get('invoice_contact').value !== (originalDetails.Invoice_Contact || '') ||
    this.form.get('invoice_address').value !== (originalDetails.Invoice_Address || '');
}
updateInvoiceDetails() {
  if (this.isUpdateEnabled) {
    const client = this.form.get('client').value;
    const project = this.form.get('project').value;
    const invoiceNumber = this.form.get('invoice_number').value;

    const updatePayload = {
      invoice_number: invoiceNumber,
      Invoice_Description: this.form.get('invoice_description').value,
      Invoice_Date: this.form.get('invoice_date').value,
      PO_Number: this.form.get('po_number').value,
      Invoice_Contact: this.form.get('invoice_contact').value,
      Invoice_Address: this.form.get('invoice_address').value
    };

    this.isLoading = true;
    this.updateErrorMessage = '';

    this.invoiceService.updateInvoiceDetails(updatePayload).subscribe({
      next: (response) => {
        // Handle successful update
        this.isUpdateEnabled = false;
        this.form.markAsPristine();
        // Optionally, you can show a success message or refresh the details
      },
      error: (error) => {
        console.error('Error updating invoice details:', error);
        this.updateErrorMessage = 'Failed to update invoice details';
      },
      complete: () => {
        this.isLoading = false;
      }
    });
  }
}
formatDate(dateString: string): string {
  if (!dateString) return '';

  try {
    const date = new Date(dateString);

    if (isNaN(date.getTime())) {
      console.warn('Invalid date:', dateString);
      return '';
    }

    // Format for input field (YYYY-MM-DD)
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
}
private resetInvoiceDetails() {
  // Clear invoice details and URL
  this.invoiceDetails = null;
  this.reportUrl = null;
  this.isUpdateEnabled = false;
  this.bankDetails = null;

  // Reset form values for invoice fields
  this.form.patchValue({
    invoice_description: '',
    invoice_date: '',
    po_number: '',
    invoice_contact: '',
    invoice_address: ''
  });

  // Unsubscribe from value changes if exists
  if (this.valueChangesSub) {
    this.valueChangesSub.unsubscribe();
    this.valueChangesSub = null;
  }

  this.form.markAsPristine();
}
}
